'use client';

import { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'next/navigation';

import { Matrix } from 'components/Matrix/Matrix';
import HomeHightLightCarousel from 'components/HighLightCarousel/HightLightCarousel';
import CompleteDataBottomSheetWrapper from 'components/CompleteDataBottomSheet/CompleteDataBottomSheetWrapper';

import { HASH_URL, MATRIX_TYPE, PAGES_URL } from 'constants/index';
import { TopsortIds } from 'constants/enums/Topsort';
import { GetServerSideHomePropsReturnType } from 'services/home/fetchHome';
import { UserData } from 'stores/AuthStore';
import useHome from 'hooks/useHome';
import useAuth from 'hooks/useAuth';
import locationStore from 'stores/LocationStore';
import { analyticsLabels, analyticsEvents, analytics } from 'lib/analytics';

import verifyTrustedUrlsToRedirect from 'utils/login/verifyTrustedUrlsToRedirect';

import * as styles from './Content.css';

type Props = Omit<GetServerSideHomePropsReturnType, 'error'> & {
  isAuthenticated: boolean;
  sessionData: UserData | null;
  isMobile?: boolean;
};

const INITIAL_COMPONENTS_COUNT = 4;

function HomeContentTemplate({
  isAuthenticated,
  sessionData,
  hydrationData: { homeHydration, cardPreviousView, locationHydration },
  isMobile
}: Props) {
  const homeStore = useHome();
  const authStore = useAuth();
  const searchParams = useSearchParams();

  const initialAuthState = useRef(isAuthenticated);
  const initialCity = useRef(locationHydration.selectedLocation.city);
  const isInitialLoadDone = useRef(false);

  homeStore.hydrate(homeHydration);
  authStore.hydrate({ userData: sessionData });

  useEffect(() => {
    const loadRemainingComponents = async () => {
      try {
        const data = await homeStore.getScreenData({
          afterComponentsCount: INITIAL_COMPONENTS_COUNT,
          components_limit: 30,
          include_response: false
        });

        if (data.components?.length > 0) {
          homeStore.appendComponents(data.components);
        }
        analytics.sendEvent(analyticsEvents.home_viewed, {});
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Home] Error loading remaining components:', error);
      } finally {
        isInitialLoadDone.current = true;
      }
    };

    loadRemainingComponents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const authChanged = initialAuthState.current !== authStore.isAuthenticated;
    const cityChanged =
      locationStore.selectedLocation.city !== initialCity.current;

    if (!isInitialLoadDone.current) return;
    if (!authChanged && !cityChanged) return;
    const refreshComponents = async () => {
      try {
        const data = await homeStore.getScreenData({
          components_limit: 30,
          include_response: false
        });
        homeStore.setHomeResult({
          ...homeStore.homeResult!,
          components: data.components
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error refreshing home data:', error);
      } finally {
        initialAuthState.current = authStore.isAuthenticated;
        initialCity.current = locationStore.selectedLocation.city;
      }
    };

    refreshComponents();
  }, [authStore.isAuthenticated, locationStore.selectedLocation.city]);

  useEffect(() => {
    if (!isAuthenticated) {
      const { pathname, hash } = window.location;
      if (
        pathname === PAGES_URL.login ||
        (pathname === PAGES_URL.home && hash === HASH_URL.login)
      ) {
        authStore.openSignInModal();
      } else if (
        pathname === PAGES_URL.createAccount ||
        (pathname === PAGES_URL.home && hash === HASH_URL.createAccount)
      ) {
        authStore.openSignUpModal();
      } else if (
        pathname === PAGES_URL.forgotPsw ||
        (pathname === PAGES_URL.home && hash === HASH_URL.forgotPsw)
      ) {
        authStore.openForgotPswModal();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authStore.isAuthenticated) {
      const { pathname, hash } = window.location;
      if (
        pathname === PAGES_URL.home &&
        (hash === HASH_URL.login ||
          hash === HASH_URL.createAccount ||
          hash === HASH_URL.forgotPsw)
      ) {
        const urlToRedirect = searchParams?.get('redirect') as string;
        if (urlToRedirect) {
          verifyTrustedUrlsToRedirect(urlToRedirect);
        }
      }
    }
  }, [authStore.isAuthenticated, searchParams]);

  const HightLightComponent = useMemo(
    () =>
      homeStore.homeResult?.components?.find(
        (c) => c.response_type === 'sponsored'
      ),
    [homeStore.homeResult?.components]
  );

  const bannerDefault = [
    { id: TopsortIds.BANNER_HOME_1, positionIndex: 2 },
    { id: TopsortIds.BANNER_HOME_2, positionIndex: 5 },
    { id: TopsortIds.BANNER_HOME_3, positionIndex: 8 }
  ];

  return (
    <>
      {HightLightComponent && (
        <HomeHightLightCarousel
          component={HightLightComponent}
          prefixTrackingName={MATRIX_TYPE.MATRIX}
          matrizTemplate={MATRIX_TYPE.HOME}
        />
      )}

      <div className={styles.wrapper} data-testid="home-matrix-wrapper">
        <Matrix
          components={homeStore.homeResult?.components || []}
          matrixType={homeHydration.homeResult?.template}
          matrixUuid={homeHydration.homeResult?.uuid}
          prefixTrackingName={MATRIX_TYPE.MATRIX}
          cardPreviousView={cardPreviousView || null}
          userLocation={locationHydration.selectedLocation || {}}
          isMobile={isMobile}
          bannerTopsort={bannerDefault}
          pageTitleForTracking={analyticsLabels.homePage}
        />
      </div>

      {isMobile && <CompleteDataBottomSheetWrapper />}
    </>
  );
}

export default observer(HomeContentTemplate);
